import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';

// UNAUTHENTICATED — customers reach this via a one-time signing token link.
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PublicApplicationSign() {
  const { token } = useParams();
  const [pkg, setPkg] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  const [signer, setSigner] = useState('');
  const [sigMode, setSigMode] = useState('drawn');
  const [typed, setTyped] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND}/api/application-sign/public/${token}`);
        setPkg(data);
        setSigner(data.customer_name || '');
        const opts = data.signature_options || { typed: true, drawn: true };
        setSigMode(opts.drawn ? 'drawn' : 'typed');
      } catch (e) {
        setErr(e.response?.data?.detail || 'This signing link is invalid or has expired.');
      } finally { setLoading(false); }
    })();
  }, [token]);

  const start = (e) => { drawingRef.current = true; const ctx = canvasRef.current.getContext('2d'); const { x, y } = getPos(e); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawingRef.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const { x, y } = getPos(e); ctx.lineWidth = 2.2; ctx.lineCap = 'round'; ctx.strokeStyle = '#0f172a'; ctx.lineTo(x, y); ctx.stroke(); };
  const end = () => { drawingRef.current = false; };
  const getPos = (e) => { const rect = canvasRef.current.getBoundingClientRect(); const cx = e.touches ? e.touches[0].clientX : e.clientX; const cy = e.touches ? e.touches[0].clientY : e.clientY; return { x: cx - rect.left, y: cy - rect.top }; };
  const clearCanvas = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); };
  const canvasBlank = () => { const c = canvasRef.current; if (!c) return true; const px = c.getContext('2d').getImageData(0, 0, c.width, c.height).data; for (let i = 3; i < px.length; i += 4) if (px[i] !== 0) return false; return true; };

  const submit = async () => {
    if (!signer.trim()) return toast.error('Please enter your full name');
    if (!confirmed) return toast.error('Please confirm the details are correct');
    let sig_data = '';
    if (sigMode === 'typed') { if (!typed.trim()) return toast.error('Please type your signature'); sig_data = typed.trim(); }
    else { if (canvasBlank()) return toast.error('Please draw your signature'); sig_data = canvasRef.current.toDataURL('image/png'); }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND}/api/application-sign/public/${token}/sign`, {
        signer_name: signer.trim(), signature_type: sigMode, signature_data: sig_data,
      });
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Signing failed. Please try again.');
    } finally { setSubmitting(false); }
  };

  if (loading) return <PageWrap><p className="text-slate-500">Loading application…</p></PageWrap>;
  if (err) return <PageWrap><Notice tone="error" title="Link unavailable">{err}</Notice></PageWrap>;
  if (done) return (
    <PageWrap><Notice tone="ok" title="Thank you">
      Your application has been signed and returned to <strong>{pkg.company_name}</strong>, who will now submit it to the church / cemetery. You may close this window.
    </Notice></PageWrap>
  );

  const opts = pkg.signature_options || { typed: true, drawn: true };

  return (
    <PageWrap>
      <div className="mb-5">
        <div className="text-sm uppercase tracking-widest text-slate-500">{pkg.company_name}</div>
        <h1 className="text-3xl font-serif text-slate-900 mt-1">{pkg.title}</h1>
        {pkg.order_number && <div className="text-sm text-slate-500 mt-1">Order <span className="font-mono">{pkg.order_number}</span> · {pkg.customer_name}</div>}
        <p className="text-sm text-slate-600 mt-3">Please review your memorial application below, then sign at the bottom. Once signed, we'll submit it to the church / cemetery on your behalf.</p>
      </div>

      <Card className="border-slate-200 shadow-sm mb-6">
        <CardContent className="p-2">
          <object data={`${BACKEND}/api/application-sign/public/${token}/pdf`} type="application/pdf"
                  width="100%" height="560" aria-label="application-pdf" data-testid="application-pdf-view">
            <div className="p-4 text-sm text-slate-600">
              Can't preview the PDF here.{' '}
              <a className="text-blue-700 underline" href={`${BACKEND}/api/application-sign/public/${token}/pdf`} target="_blank" rel="noreferrer">Open the application in a new tab</a>.
            </div>
          </object>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-6 space-y-4">
          <div>
            <label className="text-sm font-semibold text-slate-700">Your full name</label>
            <Input value={signer} onChange={e => setSigner(e.target.value)} placeholder="e.g. Jane Smith" data-testid="app-signer-name" className="mt-1" />
          </div>

          {opts.typed && opts.drawn && (
            <div className="flex gap-2">
              <Button type="button" variant={sigMode === 'drawn' ? 'default' : 'outline'} onClick={() => setSigMode('drawn')} className={sigMode === 'drawn' ? 'bg-slate-900' : ''} data-testid="app-sig-mode-drawn">Draw Signature</Button>
              <Button type="button" variant={sigMode === 'typed' ? 'default' : 'outline'} onClick={() => setSigMode('typed')} className={sigMode === 'typed' ? 'bg-slate-900' : ''} data-testid="app-sig-mode-typed">Type Signature</Button>
            </div>
          )}

          {sigMode === 'typed' ? (
            <div>
              <label className="text-sm text-slate-700">Type your name as a signature</label>
              <input value={typed} onChange={e => setTyped(e.target.value)} className="w-full mt-1 px-3 py-3 border rounded font-serif italic text-2xl text-slate-900 bg-slate-50" placeholder="Your Name" data-testid="app-typed-signature" />
            </div>
          ) : (
            <div>
              <label className="text-sm text-slate-700">Draw your signature below</label>
              <div className="mt-1 border-2 border-dashed border-slate-300 rounded bg-white">
                <canvas ref={canvasRef} width={600} height={180} onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end} onTouchStart={start} onTouchMove={move} onTouchEnd={end} style={{ width: '100%', height: 180, touchAction: 'none' }} data-testid="app-drawn-canvas" />
              </div>
              <button type="button" onClick={clearCanvas} className="mt-2 text-xs text-slate-500 hover:text-slate-800 underline" data-testid="app-clear-signature">Clear</button>
            </div>
          )}

          <label className="flex items-start gap-3 p-3 bg-white border rounded cursor-pointer hover:bg-slate-50">
            <input type="checkbox" className="mt-0.5" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} data-testid="app-confirm-check" />
            <span className="text-sm text-slate-800">I confirm the details in this application are correct and I authorise it to be submitted to the church / cemetery on my behalf.<span className="text-red-600 ml-1">*</span></span>
          </label>

          <Button onClick={submit} disabled={submitting} className="w-full bg-slate-900 hover:bg-slate-800 text-white h-12 text-base" data-testid="app-submit-signature">
            {submitting ? 'Signing…' : 'Sign & Submit Application'}
          </Button>
        </CardContent>
      </Card>
    </PageWrap>
  );
}

function PageWrap({ children }) {
  return <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-10 px-4"><div className="max-w-3xl mx-auto">{children}</div></div>;
}
function Notice({ tone, title, children }) {
  const cls = tone === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-red-50 border-red-200 text-red-900';
  return <div className={`border rounded-lg p-6 ${cls}`}><h2 className="text-lg font-semibold mb-2">{title}</h2><div className="text-sm">{children}</div></div>;
}
